import React, { useState, useEffect, useRef } from "react";
import styles from "./AddAssigneesModal.module.scss";
import classNames from "classnames/bind";
import { SearchIcon, ArrowIcon } from "~/assets/icons";
import userManagementApi, { IUser } from "~/apis/userManagement";
import { notify } from "~/utils/helper";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { IOverviewTeamMember } from "~/utils/interface/jobDetails";
import jobDetailsApi from "~/apis/jobDetails";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";

const cx = classNames.bind(styles);

interface AddAssigneesModalProps {
  isOpen: boolean;
  jobId: string;
  existingAssignees: IOverviewTeamMember[];
  onClose: () => void;
  onSave: () => void;
}

const AddAssigneesModal: React.FC<AddAssigneesModalProps> = ({
  isOpen,
  jobId,
  existingAssignees,
  onClose,
  onSave,
}) => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await userManagementApi.getUsers();

        // Handle different response structures
        let usersList: IUser[] = [];

        if (Array.isArray(response.data?.data)) {
          // Response is array directly in data
          usersList = response.data.data;
        } else if (response.data?.data?.responses) {
          // Response has responses property
          usersList = response.data.data.responses;
        } else if (Array.isArray(response.data)) {
          // Response data is array directly
          usersList = response.data;
        }

        setUsers(usersList);

        // Pre-select existing assignees
        const existingIds = existingAssignees
          .map((assignee) => assignee.account?.id)
          .filter((id): id is string => !!id);
        setSelectedUserIds(existingIds);
      } catch (error) {
        notify("error", "Failed to fetch users");
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchUsers();
      setSearchQuery("");
    }
  }, [existingAssignees, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getFilteredUsers = () => {
    if (!searchQuery.trim()) {
      return users;
    }

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user?.name?.toLowerCase()?.includes(query) ||
        user?.email?.toLowerCase()?.includes(query)
    );
  };

  const getSelectedUsers = () => {
    return users.filter((user) => selectedUserIds.includes(user.id));
  };

  const isExistingAssignee = (userId: string) => {
    return existingAssignees.some(
      (assignee) => assignee.account?.id === userId
    );
  };

  const handleSave = async () => {
    if (selectedUserIds.length === 0) {
      notify("warning", "Please select at least one assignee");
      return;
    }

    const accountIds = selectedUserIds.map((id) => parseInt(id));
    setIsSaving(true);
    try {
      await jobDetailsApi.addAssignees(jobId, { accountIds });

      notify("success", "Assignees added successfully");
      onSave();
      onClose();
    } catch (error) {
      notify("error", "Failed to add assignees");
      console.error("Error adding assignees:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const filteredUsers = getFilteredUsers();
  const selectedUsers = getSelectedUsers();

  return (
    <BaseModalV2
      open={isOpen}
      onClose={onClose}
      width="90vw"
      maxWidth="600px"
      title="Add Assignee (S)"
    >
      <div className={cx("modal-container")}>
        <div className={cx("modal-body")}>
          <div className={cx("form-group")}>
            <label className={cx("field-label")}>Select Assignee (S)</label>

            {isLoading ? (
              <div className={cx("loading-text")}>Loading users...</div>
            ) : users.length === 0 ? (
              <div className={cx("loading-text")}>
                No available users to assign
              </div>
            ) : (
              <div className={cx("assignees-selector")} ref={dropdownRef}>
                {/* Dropdown Trigger */}
                <div
                  className={cx("dropdown-trigger", { open: isDropdownOpen })}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <div className={cx("selected-display")}>
                    {selectedUsers.length > 0 ? (
                      <span className={cx("selected-text")}>
                        {selectedUsers.map((user) => user.name).join(", ")}
                      </span>
                    ) : (
                      <span className={cx("placeholder")}>
                        Select assignees...
                      </span>
                    )}
                  </div>
                  <ArrowIcon
                    className={cx("arrow-icon", { rotated: isDropdownOpen })}
                  />
                </div>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className={cx("dropdown-menu")}>
                    {/* Search Input */}
                    <div className={cx("search-container")}>
                      <SearchIcon className={cx("search-icon")} />
                      <input
                        type="text"
                        className={cx("search-input")}
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* User List */}
                    <div className={cx("user-list")}>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => {
                          const isSelected = selectedUserIds.includes(user.id);
                          const isExisting = isExistingAssignee(user.id);
                          return (
                            <label
                              key={user.id}
                              className={cx("user-checkbox-label", {
                                selected: isSelected,
                                existing: isExisting,
                              })}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleUserToggle(user.id)}
                                className={cx("user-checkbox")}
                              />
                              <span className={cx("user-name")}>
                                {user.name}
                                {isExisting && (
                                  <span className={cx("existing-badge")}>
                                    {" "}
                                    (Already assigned)
                                  </span>
                                )}
                              </span>
                              {user.email && (
                                <span className={cx("user-email")}>
                                  ({user.email})
                                </span>
                              )}
                            </label>
                          );
                        })
                      ) : (
                        <div className={cx("no-users")}>
                          {searchQuery
                            ? "No users found"
                            : "No available users to assign"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={cx("modal-footer")}>
          <PrimaryButton
            title="Cancel"
            onClick={onClose}
            disabled={isSaving}
            className={cx("cancel-button")}
          />

          <PrimaryButton
            title="Save"
            onClick={handleSave}
            disabled={isSaving || selectedUserIds.length === 0}
            className={cx("save-button")}
            loading={isSaving}
          />
        </div>
      </div>
    </BaseModalV2>
  );
};

export default AddAssigneesModal;
